import { PRODUCT_TYPE } from 'configs/constants';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchSpaceDevices } from 'store/reducers/deviceInSpaceReducers';
import { fetchFreeTrialEligibility } from 'store/reducers/freeTrialEligibilityReducers';
import { fetchAllSubscription } from 'store/reducers/subscriptionReducer';
import type {
  TFreeTrialState,
  TProductType,
  TSpaceDevicesState,
  TSpaceState,
  TSubScriptionState
} from 'typings';
import { findActiveNonPreviewSubscription } from 'utils/subscriptionFunctions';

/**
 * Who is in a Home Security purchase-flow experiment: undefined while it can't
 * be told yet, then true or false.
 *
 * Always out (control, no exposure):
 *  - a space with a 4G camera — it buys Combo plans, which the new designs
 *    don't cover yet;
 *  - a user with a real subscription — they're sent straight to the upgrade
 *    flow, so they'd be counted without ever seeing the screen being tested.
 * With `trialOnly`, also out:
 *  - a user who can't start a free trial — the new designs are built around
 *    the trial, and the current screens have a separate paid wording for them.
 *
 * Every answer comes from a fresh request started here, on mount. The slices
 * can't be trusted as they stand: the webview wipes persisted state on every
 * open, and AppBridge only asks for devices after the space call returns, so
 * "not loading, no 4G camera" can simply mean "not fetched yet". Fetching here
 * also means the requests run alongside the flag wait instead of after it.
 */
function useHomeEligibility({
  trialOnly
}: {
  trialOnly: boolean;
}): boolean | undefined {
  const dispatch = useAppDispatch();
  const { spaceData, isLoading: isSpaceLoading } = useAppSelector<TSpaceState>(
    (state) => state.space
  );
  const { is4gHomeCameraAvailable, isLoading: isDevicesLoading } =
    useAppSelector<TSpaceDevicesState>((state) => state.spaceDevices);
  const { homeSecuritySubscriptionList, isLoading: isSubscriptionLoading } =
    useAppSelector<TSubScriptionState>((state) => state.subscription);
  const {
    isEligibleForFreeTrialHomeSecurity: isTrialEligible,
    isLoading: isTrialLoading
  } = useAppSelector<TFreeTrialState>((state) => state.trial);

  const spaceId = spaceData?.id;
  // Set right after dispatching; the thunks flip their slices to loading
  // synchronously, so from the next render "not loading" means "answered".
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (!spaceId) return;
    dispatch(fetchSpaceDevices(spaceId));
    // dedupe: collapses into AppBridge's pre-warm when that's still in flight.
    dispatch(fetchAllSubscription({ dedupe: true }));
    if (trialOnly) {
      dispatch(
        fetchFreeTrialEligibility({
          productType: PRODUCT_TYPE.HOME_SECURITY as TProductType
        })
      );
    }
    setRequested(true);
  }, [dispatch, spaceId, trialOnly]);

  // No space to ask about: let control's own handling deal with it rather than
  // holding the loader forever.
  if (!spaceId) return isSpaceLoading ? undefined : false;
  if (!requested || isDevicesLoading || isSubscriptionLoading) return undefined;
  if (trialOnly && isTrialLoading) return undefined;

  return (
    !is4gHomeCameraAvailable &&
    !findActiveNonPreviewSubscription(homeSecuritySubscriptionList) &&
    (!trialOnly || isTrialEligible)
  );
}

/** Plan list experiment: every Wi-Fi user without a real plan. */
export const usePlanSelectionEligible = () =>
  useHomeEligibility({ trialOnly: false });

/** Promotion page experiment: the same, and able to start a free trial. */
export const usePromotionEligible = () =>
  useHomeEligibility({ trialOnly: true });
